import { useSuspenseQuery } from '@tanstack/react-query';
import React, { Suspense } from 'react';

import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { useLatchedCategoryFirstDecision } from '@/hooks/flags/useLatchedCategoryFirstDecision';
import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { useScreenWidth } from '@/hooks/shared/useScreenWidth';
import { ChooseServiceScreen } from '@/screens/booking/ChooseServiceScreen';
import { getServiceGridColumns } from '@/utils/booking/getServiceGridColumns';

import { ServiceGrid } from '../ServiceGrid';
import { ServiceGridSkeleton } from '../ServiceGridSkeleton';
import { ServiceGridSkeletonV2 } from '../ServiceGridSkeletonV2';
import { ServiceListV2 } from '../ServiceListV2';
import type { ChooseServiceWrapperProps } from './types';

/**
 * Decides, from the loaded shop, which list and skeleton the service step
 * uses, so the screen itself stays variant-agnostic. Category-first always
 * uses the V2 list; otherwise `expo-booking-flow-list-view` decides.
 */
export function ChooseServiceWrapper(props: ChooseServiceWrapperProps) {
  const { data: shop } = useSuspenseQuery(shopDetailQuery(props.shopId));
  const isCategoryFirstEnabled = useLatchedCategoryFirstDecision(shop);
  const isNewServiceUI = useLaunchDarklyFlag(
    'expo-booking-flow-list-view',
    false,
  );
  const screenWidth = useScreenWidth();

  const isListView = isCategoryFirstEnabled === true || isNewServiceUI;
  const skeleton =
    isCategoryFirstEnabled === null || isListView ? (
      <ServiceGridSkeletonV2 />
    ) : (
      <ServiceGridSkeleton columns={getServiceGridColumns(screenWidth)} />
    );

  return (
    <Suspense fallback={skeleton}>
      {isCategoryFirstEnabled === null ? (
        skeleton
      ) : (
        <ChooseServiceScreen
          {...props}
          ListComponent={isListView ? ServiceListV2 : ServiceGrid}
          isCategoryFirstEnabled={isCategoryFirstEnabled}
        />
      )}
    </Suspense>
  );
}
